import React, { useEffect, useState, useRef } from "react";
import { Form, notification, Modal, type DatePickerProps, type TableColumnsType, type TableProps } from "antd";
import axios from "axios";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

interface TableDataType {
     key: React.Key;
     item_code: string;
     item_name: string;
     case: number;
     uom: string;
     uom_rate: number;
     location: string;
     ref: string;
     do: string;
     receive: number;
     return_qty: number;
     qty: number;
     return: number;
     item_price: number;
     remark: string;
     do_no: string;
     discount_per_item?: string
}

interface ModalDataType {
     key: React.Key;
     receive_no: string;
     date: string;
     supplier: string;
     supplier_name: string;
     supplier_invoice_no: string;
     tax_invoice_no: string;
     warehouse: string;
}

const useGoodsReceiveReturnLogic = () => {
     const [form] = Form.useForm();
     const location = useLocation();
     const navigate = useNavigate();
     const { userData } = useAuth();
     const [isModalOpen, setIsModalOpen] = useState(false);
     const [isEditModalOpen, setIsEditModalOpen] = useState(false);
     const [editingRecord, setEditingRecord] = useState<TableDataType | null>(null);
     const [isAddLineModalOpen, setIsAddLineModalOpen] = useState(false);
     const [tableData, setTableData] = useState<TableDataType[]>([]);
     const [isProcessing, setIsProcessing] = useState(false);
     const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'processing' | 'error' | 'stopped'>('idle');
     const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };
     const [totalReturn, setTotalReturn] = useState<number>(0)
     const [totalReturnAmount, setTotalReturnAmount] = useState<number>(0);
     const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
     const [submitRemark, setSubmitRemark] = useState("");
     const formValues = Form.useWatch([], form);
     const isGoodsReceiveDisabled = !formValues?.goods_receive_return_no || !!formValues?.goods_receive_no;
     const isSubmitDisabled = formValues?.status !== 'Draft';
     const isVoidDisabled = formValues?.status !== 'Draft' && formValues?.status !== '1';
     const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

     const statusMap: { [key: string]: string } = {
          '1': 'Draft',
          '2': 'Approved',
          '3': 'Waiting for Approved',
          '5': 'Canceled',
     };

     const statusToDbMap: { [key: string]: string } = {
          'Draft': '1',
          'Approved': '2',
          'Waiting for Approved': '3',
          'Canceled': '5',
     };

     useEffect(() => {
          const goodsReceiveReturnNo = location.state?.goodsReceiveReturnNo;
          if (goodsReceiveReturnNo) {
               const fetchData = async () => {
                    setAutosaveStatus('processing');
                    try {
                         const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-return/${goodsReceiveReturnNo}`);
                         const { header, details } = response.data;

                         const mappedHeader = {
                              ...header,
                              date: header.date ? moment(header.date) : null,
                              status: statusMap[header.status] || 'Unknown',
                         };

                         form.setFieldsValue(mappedHeader);
                         setTableData(details);
                         setAutosaveStatus('idle');
                    } catch (error) {
                         console.error("Error fetching goods receive return details:", error);
                         setAutosaveStatus('error');
                    }
               };
               fetchData();
          }
     }, [location, form]);

     useEffect(() => {
          const total = tableData.reduce((sum, item) => sum + (Number(item.return) || 0), 0);
          setTotalReturn(total);
     }, [tableData]);

     useEffect(() => {
          const total = tableData.reduce((sum, item) => {
               const returnQty = Number(item.return) || 0;
               const price = Number(item.item_price) || 0;
               return sum + (returnQty * price);
          }, 0);
          setTotalReturnAmount(total);
          form.setFieldsValue({ total_amount: total });
     }, [tableData, form]);

     if (!location.state?.goodsReceiveReturnNo) {
          form.setFieldsValue({ status: "Draft" });
     }


     const autosave = (dataToSaveInTable?: TableDataType[]) => {
          const currentStatus = form.getFieldValue('status');
          if (currentStatus !== 'Draft' && currentStatus !== '1') {
               setAutosaveStatus('stopped');
               return;
          }

          const goodsReceiveReturnNo = form.getFieldValue('goods_receive_return_no');
          if (!goodsReceiveReturnNo) {

               setAutosaveStatus('stopped');
               return;
          }

          if (autosaveTimeoutRef.current) {
               clearTimeout(autosaveTimeoutRef.current);
          }

          setAutosaveStatus('processing');
          autosaveTimeoutRef.current = setTimeout(async () => {
               try {
                    const formValues = form.getFieldsValue(true);
                    const statusForDb = statusToDbMap[formValues.status] || formValues.status;
                    const currentTableData = dataToSaveInTable || tableData;
                    const total = currentTableData.reduce((sum, item) => sum + (Number(item.item_price) || 0), 0);
                    const dataToSave = {
                         ...formValues,
                         status: statusForDb,
                         tableData: currentTableData,
                         date: formValues.date ? moment(formValues.date).format('YYYY-MM-DD') : null,
                         total_return: totalReturn,
                    };

                    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-return/autosave`, dataToSave);
                    setAutosaveStatus('idle');
               } catch (error) {
                    console.error("Autosave gagal:", error);
                    setAutosaveStatus('error');
               }
          }, 900);
     };

     useEffect(() => {
          return () => {
               if (autosaveTimeoutRef.current) {
                    clearTimeout(autosaveTimeoutRef.current);
               }
          };
     }, []);

     useEffect(() => {
          const goodsReceiveReturnNo = form.getFieldValue('goods_receive_return_no');
          const currentStatus = form.getFieldValue('status');
          if (goodsReceiveReturnNo && (currentStatus === 'Draft' || currentStatus === '1')) {
               autosave();
          }

     }, [JSON.stringify(formValues), JSON.stringify(tableData)]);

     const handleSubmit = async () => {
          try {
               await form.validateFields();
               setIsSubmitModalOpen(true);
          } catch (errorInfo) {
               console.log('Validasi gagal:', errorInfo);
          }
     };

     const handleConfirmSubmit = async () => {
          const goodsReceiveReturnNo = form.getFieldValue('goods_receive_return_no');
          if (!goodsReceiveReturnNo) {
               notification.error({ message: "Goods Receive Return No. tidak ditemukan untuk disubmit.", duration: 4 });
               return;
          }
          setIsProcessing(true);
          try {
               const totalAmount = form.getFieldValue('total_amount');
               const warehouse = form.getFieldValue('warehouse');
               await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-return/submit`, {
                    goods_receive_return_no: goodsReceiveReturnNo,
                    status: '3',
                    total_amount: totalAmount ? String(totalAmount) : '0',
                    warehouse: warehouse || '',
                    kode_grup1: userData?.kode_grup1 || '',
                    remark: submitRemark
               });
               notification.success({ message: `Dokumen ${goodsReceiveReturnNo} berhasil disubmit.`, duration: 4 });
               setIsSubmitModalOpen(false);
               setSubmitRemark("");
               navigate('/dashboard/data_goods_receive_return');
          } catch (error) {
               console.error("Gagal melakukan submit:", error);
               notification.error({ message: "Terjadi kesalahan saat melakukan submit.", duration: 4 });
          } finally {
               setIsProcessing(false);
          }
     };

     const handleVoid = () => {
          const grrNo = form.getFieldValue('goods_receive_return_no');
          if (!grrNo) return;
          Modal.confirm({
               title: 'Konfirmasi Void',
               content: `Apakah Anda yakin ingin menghapus data ${grrNo}? Tindakan ini tidak dapat dibatalkan.`,
               okText: 'Ya, Void',
               okType: 'danger',
               onOk: async () => {
                    try {
                         await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-return/void`, { goods_receive_return_no: grrNo });
                         notification.success({ message: `Data ${grrNo} berhasil dihapus.` });
                         navigate('/dashboard/data_goods_receive_return');
                    } catch (error) {
                         notification.error({ message: "Gagal menghapus data." });
                    }
               },
          });
     };

     const showModal = () => {
          setIsModalOpen(true);
     };

     const handleCancel = () => {
          setIsModalOpen(false);
     };

     const showAddLineModal = () => {
          setIsAddLineModalOpen(true);
     };

     const handleAddLineCancel = () => {
          setIsAddLineModalOpen(false);
     };

     const handleAddLineOk = (selectedRecords: TableDataType[]) => {
          setTableData((prevData) => {
               const newRecords = selectedRecords.filter(
                    (newRec) => !prevData.some((existingRec) => existingRec.key === newRec.key)
               );
               const updatedData = [...prevData, ...newRecords];
               autosave(updatedData);
               return updatedData;
          });
          handleAddLineCancel();
     };

     const handleTableDoubleClick = (record: TableDataType) => {
          setEditingRecord(record);
          setIsEditModalOpen(true);
     };

     const handleEditCancel = () => {
          setIsEditModalOpen(false);
          setEditingRecord(null);
     };

     const handleUpdateRecord = (updatedRecord: TableDataType) => {
          const updatedData = tableData.map(item => item.key === updatedRecord.key ? updatedRecord : item);
          setTableData(updatedData);
          handleEditCancel();
          autosave(updatedData);
     };

     const handleRowDoubleClick = async (record: ModalDataType) => {
          setIsProcessing(true);
          form.setFieldsValue({
               goods_receive_no: record.receive_no,
               supplier_code: record.supplier,
               supplier_name: record.supplier_name,
               supplier_invoice_no: record.supplier_invoice_no,
               tax_invoice_no: record.tax_invoice_no,
               warehouse: record.warehouse,
          });

          try {
               const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-details/${record.receive_no}`,
               );
               const newTableData = response.data.filter((item: TableDataType) => {
                    const sisa = Number(item.receive || 0) - Number(item.return_qty || 0);
                    return sisa > 0;
               });
               if (newTableData.length > 0) {
                    setTableData(newTableData);
                    autosave(newTableData);
               }
          } catch (error) {
               console.error("Error fetching goods receive details:", error);
               setTableData([]);
               setAutosaveStatus('error');
          } finally {
               setIsProcessing(false);
               setIsModalOpen(false);
          }
          setIsModalOpen(false);
     };
     const onDateChange: DatePickerProps["onChange"] = (date, dateString) => {
          console.log("Selected Date: ", dateString);
     };

     const onChange = (value: string) => {
          console.log(`Selected: ${value}`);

     };

     const onItemGroupChange = async (value: string) => {
          if (value !== "") {
               try {
                    const response = await axios.get(
                         `${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-return-no`,
                    );
                    const generatedNo = response.data.goodsReceiveReturnNo;
                    form.setFieldsValue({ goods_receive_return_no: generatedNo, item_group: value });
                    autosave();
               } catch (error) {
                    console.error("Error generating Goods Receive Return No:", error);
                    form.setFieldsValue({ goods_receive_return_no: "Error generating number", item_group: value });
                    setAutosaveStatus('error');
               }
          } else {
               form.setFieldsValue({ goods_receive_return_no: "", item_group: value });
               setAutosaveStatus('stopped');
          }
     };

     const columns: TableColumnsType<TableDataType> = [
          { title: "Item Code", dataIndex: "item_code" },
          { title: "Item Name", dataIndex: "item_name" },
          { title: "Case", dataIndex: "case" },
          { title: "UOM", dataIndex: "uom" },
          { title: "UoM Rate", dataIndex: "uom_rate" },
          { title: "Location", dataIndex: "location" },
          { title: "Ref", dataIndex: "ref" },
          { title: "DO", dataIndex: "do" },
          { title: "Receive", dataIndex: "receive" },
          { title: "Return", dataIndex: "return" },
          {
               title: "Item Price",
               dataIndex: "item_price",
               render: (text: number) => new Intl.NumberFormat("id-ID").format(text),
          },
          { title: "Remark", dataIndex: "remark" },
          { title: "DO No", dataIndex: "do_no" },
     ];

     const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
     const rowSelection: TableProps<TableDataType>["rowSelection"] = {
          selectedRowKeys,
          onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
     };

     const handleDeleteSelectedRows = () => {
          setTableData(prevData => {
               const updatedData = prevData.filter(item => !selectedRowKeys.includes(item.key));
               autosave(updatedData);
               return updatedData;
          });
          setSelectedRowKeys([]);
     };

     const handlePrint = async () => {
          const goodsReceiveReturnNo = form.getFieldValue('goods_receive_return_no');
          if (!goodsReceiveReturnNo) {
               notification.error({ message: "Goods Receive Return No. tidak ditemukan untuk dicetak.", duration: 4 });
               return;
          }

          try {
               const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-return/print-data/${goodsReceiveReturnNo}`);
               const { header, details } = response.data;

               const reportUrl = `${process.env.PUBLIC_URL}/reports/GR_return/index.html`;
               const templateResponse = await fetch(reportUrl);
               let htmlContent = await templateResponse.text();
               htmlContent = htmlContent.replace('<head>', '<head><base href="/reports/GR_return/">');
               htmlContent = htmlContent.replace('fields_supplier', header.supplier_name || '');
               htmlContent = htmlContent.replace('fields_address', header.address || '');
               htmlContent = htmlContent.replace('fields_invoice_no', header.supplier_invoice_no || '');
               htmlContent = htmlContent.replace('fields_tax_invoice_no', header.tax_invoice_no || '');
               htmlContent = htmlContent.replace('fields_purchase_order', header.ref_document_no || '');
               htmlContent = htmlContent.replace('fields_doc_no', header.goods_receive_return_no || '');
               htmlContent = htmlContent.replace('fields_doc_date', header.date ? moment(header.date).format('DD/MM/YYYY') : '');
               htmlContent = htmlContent.replace('fields_ref_no', header.goods_receive_no || '');
               htmlContent = htmlContent.replace('fields_warehouse_code', header.warehouse || '');
               htmlContent = htmlContent.replace('fiedls_remark', header.remark || '');

               let tableRows = '';
               let totalReceive = 0;
               details.forEach((item: any, index: number) => {
                    tableRows += `
                    <tr>
                        <td style="text-align: center; padding: 5px;">${index + 1}</td>
                        <td style="text-align: center; padding: 5px;">${item.item_code || ''}</td>
                        <td style="text-align: left; padding: 5px;">${item.item_name || ''}</td>
                        <td style="text-align: right; padding: 5px;">${item.case || 0}</td>
                        <td style="text-align: right; padding: 5px;">${item.uom || ''}</td>
                        <td style="text-align: right; padding: 5px;">${item.location || ''}</td>
                        <td style="text-align: right; padding: 5px;">${new Intl.NumberFormat('id-ID').format(item.receive || 0)}</td>
                        <td style="text-align: right; padding: 5px;">${new Intl.NumberFormat('id-ID').format(item.return || 0)}</td>
                    </tr>
                `;
                    totalReceive += Number(item.receive) || 0;
               });

               const tbodyRegex = /<tbody>[\s\S]*?<\/tbody>/;
               htmlContent = htmlContent.replace(tbodyRegex, `<tbody>${tableRows}</tbody>`);

               htmlContent = htmlContent.replace('fields_total_receive', new Intl.NumberFormat('id-ID').format(totalReceive));
               htmlContent = htmlContent.replace('fields_total_return', new Intl.NumberFormat('id-ID').format(header.total_return || 0));

               const today = new Date();
               const formattedDate = `${today.getDate()} ${today.toLocaleString('id-ID', { month: 'long' })} ${today.getFullYear()}`;
               htmlContent = htmlContent.replace('dd mm yyyy', formattedDate);

               const printWindow = window.open('', '_blank');
               if (printWindow) {
                    printWindow.document.write(htmlContent);
                    printWindow.document.close();
               } else {
                    notification.error({ message: "Gagal membuka jendela cetak. Mohon izinkan pop-up untuk situs ini.", duration: 4 });
               }

          } catch (error) {
               console.error("Gagal mempersiapkan data cetak:", error);
               notification.error({ message: "Terjadi kesalahan saat mempersiapkan data untuk dicetak.", duration: 4 });
          }
     };

     return {
          transformStyle,
          onDateChange,
          onChange,
          columns,
          rowSelection,
          form,
          onItemGroupChange,
          isModalOpen,
          showModal,
          handleCancel,
          tableData,
          handleRowDoubleClick,
          isEditModalOpen,
          handleEditCancel,
          editingRecord,
          handleTableDoubleClick,
          handleUpdateRecord,
          isAddLineModalOpen,
          showAddLineModal,
          handleAddLineCancel,
          handleAddLineOk,
          handleDeleteSelectedRows,
          autosaveStatus,
          totalReturn,
          totalReturnAmount,
          handleSubmit,
          isGoodsReceiveDisabled,
          handlePrint,
          isSubmitDisabled,
          isSubmitModalOpen,
          setIsSubmitModalOpen,
          submitRemark,
          setSubmitRemark,
          handleConfirmSubmit,
          handleVoid,
          isVoidDisabled,
          isProcessing
     };
};

export default useGoodsReceiveReturnLogic;